import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createLegalEntity,
  disableBlock,
  enableBlock,
  fetchFilialOptions,
  fetchFilialScreen,
  fetchFilialSettingHistory,
  fetchLegalEntities,
  fetchNetworkScreen,
  fetchNetworkSettingHistory,
  overrideSetting,
  setHours,
  setLegalEntity,
  setNetworkSetting,
  setReceptionHours,
  trashLegalEntity,
  updateLegalEntity,
  type FilialScreen,
  type LegalEntitiesScreen,
  type NetworkScreen,
} from './api';

export const NETWORK_KEY = ['network', 'screen'] as const;
export const FILIAL_OPTIONS_KEY = ['network', 'filial-options'] as const;
export const filialKey = (id: number) => ['network', 'filial', id] as const;

export function useNetworkScreen() {
  return useQuery({ queryKey: NETWORK_KEY, queryFn: fetchNetworkScreen });
}

export function useFilialScreen(id: number | null) {
  return useQuery({
    queryKey: filialKey(id ?? 0),
    queryFn: () => fetchFilialScreen(id as number),
    enabled: id !== null,
  });
}

/**
 * Опции фильтра «Вся сеть ▾».
 *
 * ⛔ Список приходит С СЕРВЕРА, а не собирается фронтом из справочника: он же
 * отвечает за «Вся сеть» первой строкой и за отсев архивных. Собери его здесь —
 * и появилась бы вторая копия правила «старт — вся сеть», которое канон
 * объявил один раз (И-Ф12, «мой город по умолчанию запрещён»).
 */
export function useFilialOptionsFilter() {
  return useQuery({ queryKey: FILIAL_OPTIONS_KEY, queryFn: fetchFilialOptions, staleTime: 60_000 });
}

/**
 * Общий вид мутаций карточки: ответ — сама карточка, она и кладётся в кэш.
 *
 * Сеть тоже обновляется: переопределение брони филиала меняет его строку на
 * Э-Н1, и оставить там вчерашнее число значило бы показать владельцу два разных
 * ответа на один вопрос на соседних экранах.
 */
function useFilialMutation<TArgs>(fn: (args: TArgs) => Promise<FilialScreen>) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: fn,
    onSuccess: (screen) => {
      qc.setQueryData(filialKey(screen.filial.id), screen);
      void qc.invalidateQueries({ queryKey: NETWORK_KEY });
    },
  });
}

export function useOverrideSetting(filialId: number) {
  return useFilialMutation(({ key, value }: { key: string; value: unknown }) =>
    overrideSetting(filialId, key, value),
  );
}

export function useSetLegalEntity(filialId: number) {
  return useFilialMutation((legalEntityId: number) => setLegalEntity(filialId, legalEntityId));
}

export function useSetHours(filialId: number) {
  return useFilialMutation(({ start, end }: { start: string | null; end: string | null }) =>
    setHours(filialId, start, end),
  );
}

export function useEnableBlock(filialId: number) {
  return useFilialMutation((block: string) => enableBlock(filialId, block));
}

export function useDisableBlock(filialId: number) {
  return useFilialMutation(({ block, reason }: { block: string; reason: string }) =>
    disableBlock(filialId, block, reason),
  );
}

/**
 * Текст отказа гейта — владельцу СЛОВАМИ гейта.
 *
 * Сервер бросает 422 с готовым человеческим объяснением («за блоком числится 12
 * фактов»). Подменить его на «Ошибка сохранения» значило бы выбросить ровно ту
 * часть, ради которой гейт писали.
 */
/**
 * СВОД ГЕЙТА ИЗ ОТКАЗА — КОДЫ, ПОЗИЦИИ И АДРЕС ПРАВКИ (`s337`, Д18, владелец
 * 20.09.2026: «алерт надо выдавать там где править надо а не искать по всему
 * экрану»).
 *
 * ⛔ ОТКАЗ ЗАКРЫТИЯ ВЕЗЁТ РОВНО ТО ЖЕ, ЧТО ЭКРАН ДО НАЖАТИЯ. Прежде из ответа
 * брался только склеенный текст, и экран ПОСЛЕ нажатия знал меньше, чем знал
 * ДО: коды и позиции терялись по дороге (process-rules §38).
 */
export function refusalGate(error: unknown): unknown {
  const e = error as { response?: { data?: { gate?: unknown } } };

  return e?.response?.data?.gate ?? null;
}

/**
 * ПОВОДЫ ОТКАЗА «НУЖНА ПРИЧИНА ПЕРЕНОСА» (`s338`, Д4).
 *
 * ⛔⛔ ЭКРАН НЕ СЧИТАЕТ, СПРАШИВАТЬ ЛИ, — ОН СПРАШИВАЕТ У СЕРВЕРА. Правило
 * владельца («спрашивают только при смене мастера или даты») живёт в одном
 * месте, своде `PerenosPravilo`; посчитай его ещё раз в браузере — и первая же
 * правка границы разведёт два ответа. Поэтому плитка уходит на сервер как
 * есть, а окно причины открывается ровно тогда, когда пришёл ЭТОТ отказ.
 * Ход при этом не состоялся: запись осталась на своём месте.
 */
export function refusalPerenos(error: unknown): { kody: string[] } | null {
  const e = error as { response?: { data?: { perenos?: { prichina_nuzhna?: boolean; kody?: string[] } } } };
  const blok = e?.response?.data?.perenos;

  return blok?.prichina_nuzhna === true ? { kody: Array.isArray(blok.kody) ? blok.kody : [] } : null;
}

/**
 * ОТКАЗ, КОТОРЫЙ СНИМАЕТ РЕЖИМ ПРИЁМКИ (`s338`, 21.09.2026).
 *
 * ⛔⛔ ЭКРАН НЕ УГАДЫВАЕТ ЭТО ПО ТЕКСТУ ФРАЗЫ. Режим снимает только те отказы,
 * что прошли через `BookingTransition::otkaz()`; прочие остаются боевыми.
 * Признак ставит сервер и он же проверяет право — кому режим недоступен, тому
 * и ссылки не показывается (op-rules §31.5): обещать дверь, которая для
 * человека закрыта, хуже, чем молчать.
 *
 * Повод — слово владельца: «добавь для меня лично кнопочку где-то
 * „игнорировать блокер" )) потому что блокер верный но как тестить то?».
 * Второго переключателя не заводится (§31.8) — заводится дорога к единственному.
 */
export function refusalPriyomka(error: unknown): { pravilo: string } | null {
  const e = error as { response?: { data?: { priyomka?: { snimaet?: boolean; pravilo?: string } } } };
  const blok = e?.response?.data?.priyomka;

  return blok?.snimaet === true ? { pravilo: String(blok.pravilo ?? '') } : null;
}

/*
 * TODO-slovar (`s1027` G-13/G-17): `refusalText` — функция вне дерева React, словаря
 * экрана у неё нет. Обе фразы — временно здесь; в наливку `screen.text` идут
 * `network.otkaz.vhod_istyok` и `network.otkaz.ne_sohraneno`, после чего экраны
 * с `t` подставляют их сами.
 */
const VHOD_ISTYOK = 'Вход истёк — войдите заново';
const NE_SOHRANENO = 'Не удалось сохранить';

export function refusalText(error: unknown): string {
  if ((error as { transferCancelled?: boolean })?.transferCancelled === true) return '';
  const e = error as { sessiyaIstekla?: boolean; response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } } };

  /* ⛔ `s1027` G-13: истёкший вход — словами салона, а не «Unauthenticated.» / «CSRF token mismatch.». */
  if (e?.sessiyaIstekla === true || e?.response?.status === 401 || e?.response?.status === 419) return VHOD_ISTYOK;

  const errors = e?.response?.data?.errors;

  if (errors) {
    const first = Object.values(errors)[0];
    if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  }

  return e?.response?.data?.message ?? NE_SOHRANENO;
}

/**
 * Правка ДЕФОЛТА СЕТИ (`s105`, правка 1).
 *
 * ⛔ Карточки филиалов после неё СБРАСЫВАЮТСЯ, а не остаются в кэше. Сетевое
 * значение наследуют все, кто не завёл своё: оставь их кэш нетронутым — и
 * владелец, вернувшись в карточку, увидел бы вчерашнее число «как в сети», то
 * есть два разных ответа на один вопрос на соседних экранах.
 */
export function useSetNetworkSetting() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => setNetworkSetting(key, value),
    onSuccess: (screen: NetworkScreen) => {
      qc.setQueryData(NETWORK_KEY, screen);
      void qc.invalidateQueries({ queryKey: ['network', 'filial'] });
      void qc.invalidateQueries({ queryKey: ['network', 'history'] });
    },
  });
}

/** История правок настройки — грузится только когда строку раскрыли. */
export function useNetworkSettingHistory(key: string | null) {
  return useQuery({
    queryKey: ['network', 'history', 'network', key ?? ''],
    queryFn: () => fetchNetworkSettingHistory(key as string),
    enabled: key !== null,
  });
}

export function useFilialSettingHistory(filialId: number, key: string | null) {
  return useQuery({
    queryKey: ['network', 'history', 'filial', filialId, key ?? ''],
    queryFn: () => fetchFilialSettingHistory(filialId, key as string),
    enabled: key !== null,
  });
}

/** Часы приёма филиала (`s105`, правка 4) — служебного окна не касаются. */
export function useSetReceptionHours(filialId: number) {
  return useFilialMutation((hours: Record<string, Array<[string, string]>> | null) =>
    setReceptionHours(filialId, hours),
  );
}

/* ═══════════════════ Юрлица (`s105`, правка 5) ═══════════════════ */

export const LEGAL_ENTITIES_KEY = ['network', 'legal-entities'] as const;

export function useLegalEntities() {
  return useQuery({ queryKey: LEGAL_ENTITIES_KEY, queryFn: fetchLegalEntities });
}

/**
 * Любая правка юрлица возвращает СПИСОК целиком и попутно сбрасывает карточки
 * филиалов: название юрлица стоит и там, и переименование обязано доехать.
 */
function useLegalEntityMutation<TArgs>(fn: (args: TArgs) => Promise<LegalEntitiesScreen>) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: fn,
    onSuccess: (screen) => {
      qc.setQueryData(LEGAL_ENTITIES_KEY, screen);
      void qc.invalidateQueries({ queryKey: NETWORK_KEY });
      void qc.invalidateQueries({ queryKey: ['network', 'filial'] });
    },
  });
}

export function useCreateLegalEntity() {
  return useLegalEntityMutation(({ name, inn }: { name: string; inn: string }) =>
    createLegalEntity(name, inn),
  );
}

export function useUpdateLegalEntity() {
  return useLegalEntityMutation(({ id, name, inn }: { id: number; name: string; inn: string }) =>
    updateLegalEntity(id, name, inn),
  );
}

export function useTrashLegalEntity() {
  return useLegalEntityMutation(({ id, reason }: { id: number; reason: string }) =>
    trashLegalEntity(id, reason),
  );
}
